import { availabilityLabel } from '@shared/contracts/receivable-availability'
import { analyticsDate, ANALYTICS_TIME_ZONE } from '@shared/lib/analytics-period'
import type { StatusTone } from '@shared/design-system/ui'
import { roundMoneyForDisplay } from '@shared/lib/money/display-reconciliation'
import type { StatementDirection, StatementEntry, StatementEventType } from '../api/wallet.contract'

export type StatementVisualDirection = 'in' | 'held' | 'out' | 'info'

export function statementVisualDirection(
  direction: StatementDirection,
  _eventType?: StatementEventType,
): StatementVisualDirection {
  const directions: Record<StatementDirection, StatementVisualDirection> = {
    IN: 'in',
    OUT: 'out',
    HELD: 'held',
    INFO: 'info',
  }
  return directions[direction]
}

export function statementVisualTone(
  direction: StatementVisualDirection,
  _eventType?: StatementEventType,
): StatementVisualDirection {
  return direction
}

export function statementSignPrefix(direction: StatementVisualDirection): string {
  if (direction === 'in') {
    return '+'
  }
  if (direction === 'out') {
    return '−'
  }
  return ''
}

export function statementEventTypeLabel(
  type: StatementEventType,
  receiptsExperience = false,
): string {
  const labels: Record<StatementEventType, string> = {
    WITHDRAWAL_OBSERVED: receiptsExperience ? 'Saque automático' : 'Repasse observado na Stripe',
    SALE_SETTLED: 'Venda',
    SALE_REVOKED: 'Venda revertida',
    DEBT_CREATED: 'Débito',
    DEBT_SETTLED: 'Débito',
    DEBT_REVERSED: 'Dívida cancelada',
    WITHDRAWAL_REQUESTED: 'Saque solicitado',
    WITHDRAWAL_APPROVED: 'Saque aprovado',
    WITHDRAWAL_REFUSED: 'Saque recusado',
    WITHDRAWAL_COMPLETED: 'Repasse conciliado',
    WITHDRAWAL_REVERTED: 'Repasse revertido na carteira',
    CONVERSION: 'Conversão',
  }
  return labels[type]
}

export function statementEventTone(type: StatementEventType): StatusTone {
  if (
    type === 'SALE_SETTLED' ||
    type === 'DEBT_REVERSED' ||
    type === 'WITHDRAWAL_APPROVED' ||
    type === 'WITHDRAWAL_COMPLETED'
  ) {
    return 'success'
  }
  if (type === 'DEBT_CREATED' || type === 'DEBT_SETTLED' || type === 'SALE_REVOKED') {
    return 'informative'
  }
  if (type === 'WITHDRAWAL_REFUSED' || type === 'WITHDRAWAL_REVERTED') {
    return 'closed'
  }
  return 'neutral'
}

export function statementIsDebt(entry: StatementEntry): boolean {
  return ['DEBT_CREATED', 'DEBT_SETTLED', 'DEBT_REVERSED', 'SALE_REVOKED'].includes(entry.eventType)
}

export function statementTitle(entry: StatementEntry, receiptsExperience = false): string {
  if (entry.eventType === 'SALE_SETTLED' && entry.detail.role === 'referral')
    return 'Comissão de indicação'
  if (entry.eventType === 'SALE_SETTLED') return 'Crédito da venda'
  if (entry.eventType === 'WITHDRAWAL_OBSERVED')
    return receiptsExperience ? 'Saque automático' : 'Repasse observado na Stripe'
  if (entry.eventType === 'WITHDRAWAL_COMPLETED') return 'Repasse conciliado na carteira'
  if (entry.eventType === 'WITHDRAWAL_REVERTED') return 'Repasse revertido na carteira'
  const title = entry.detail.title
  return typeof title === 'string' && title.length > 0
    ? title
    : statementEventTypeLabel(entry.eventType)
}

export function statementMeta(entry: StatementEntry): string {
  const parts = [
    entry.saleId ? statementSaleReferenceFullMetaLabel(entry.saleId) : undefined,
    statementIsDebt(entry) ? undefined : statementDetailMeta(entry),
  ].filter((value): value is string => Boolean(value))
  return parts.join(' · ')
}

export function statementDetailMeta(entry: StatementEntry): string | undefined {
  const parts: string[] = []
  const userAmount = detailMoney(entry, 'userAmount')
  const reserveAmount = detailMoney(entry, 'reserveAmount')
  const remainingAmount = detailMoney(entry, 'remainingAmount')
  const sourceAmount = detailMoney(entry, 'sourceAmount')
  const availableAt = detailString(entry, 'availableAt')

  if (userAmount) {
    parts.push(
      `${entry.direction === 'IN' ? '+' : ''}${formatMoney(userAmount)} · ${availabilityLabel(entry.detail.availability, availableAt ? formatShortDate(availableAt) : undefined)}`,
    )
  }
  if (reserveAmount) {
    parts.push(`${formatMoney(reserveAmount)} retido`)
  }
  if (remainingAmount) {
    parts.push(`pendente nesta data: ${formatMoney(remainingAmount)}`)
  }
  if (sourceAmount && !userAmount) {
    parts.push(`${formatMoney(sourceAmount)} solicitado`)
  }
  return parts.length > 0 ? parts.join(' · ') : undefined
}

export function statementAmountVisible(entry: StatementEntry): boolean {
  if (entry.eventType === 'WITHDRAWAL_OBSERVED') return false
  return entry.direction !== 'INFO' || entry.amount.amount !== '0.000'
}

export function statementEntryForDisplay(entry: StatementEntry): StatementEntry {
  if (entry.eventType !== 'SALE_SETTLED' || entry.direction !== 'IN') return entry
  return { ...entry, amount: roundMoneyForDisplay(entry.amount) }
}

export function statementSummaryMovements(entries: readonly StatementEntry[]) {
  return entries.flatMap((entry) => {
    const reserve = detailMoney(entry, 'reserveAmount')
    // SALE_SETTLED exposes the reserve in detail when the main credit is IN.
    return entry.eventType === 'SALE_SETTLED' && entry.direction === 'IN' && reserve
      ? [
          { direction: entry.direction, amount: entry.amount },
          { direction: 'HELD', amount: reserve },
        ]
      : [{ direction: entry.direction, amount: entry.amount }]
  })
}

export function statementSaleIds(entry: StatementEntry): string[] {
  if (entry.detail.role === 'referral') return []
  return [
    ...new Set(
      [entry.saleId, ...entry.relatedSaleIds].filter((value): value is string => Boolean(value)),
    ),
  ]
}

export function statementSaleReferenceLabel(saleId: string): string {
  return `Venda ${statementShortSaleId(saleId)}`
}

export function statementSaleReferenceFullLabel(saleId: string): string {
  const invoice = subscriptionInvoiceReference(saleId)
  return invoice !== null
    ? `Assinatura ${invoice.subscriptionId} · Renovação #${invoice.cycle}`
    : `Venda ${saleId}`
}

export function statementSaleReferenceFullMetaLabel(saleId: string): string {
  const invoice = subscriptionInvoiceReference(saleId)
  return invoice !== null
    ? `assinatura ${invoice.subscriptionId} · renovação #${invoice.cycle}`
    : `venda ${statementShortSaleId(saleId)}`
}

export function statementShortSaleId(saleId: string): string {
  if (saleId.startsWith('funnel:')) {
    const [, origin, step] = saleId.split(':')
    if (origin && step) return `${statementShortSaleId(origin)} / ${step.slice(0, 6)}`
  }
  const invoice = subscriptionInvoiceReference(saleId)
  if (invoice) return `${statementShortSaleId(invoice.subscriptionId)} · ciclo ${invoice.cycle}`
  return saleId.length > 12 ? `${saleId.slice(0, 8)}…${saleId.slice(-6)}` : saleId
}

type SubscriptionInvoiceReference = {
  subscriptionId: string
  cycle: number
}

function subscriptionInvoiceReference(saleId: string): SubscriptionInvoiceReference | null {
  const prefix = 'invoice:'
  if (!saleId.startsWith(prefix)) {
    return null
  }
  const separator = saleId.lastIndexOf(':')
  if (separator <= prefix.length || separator === saleId.length - 1) {
    return null
  }
  const subscriptionId = saleId.slice(prefix.length, separator)
  const cycle = Number(saleId.slice(separator + 1))
  return Number.isInteger(cycle) && cycle > 0 ? { subscriptionId, cycle } : null
}

type MoneyLike = {
  amount: string
  currency: string
}

export function detailMoney(entry: StatementEntry, key: string): MoneyLike | undefined {
  const value = entry.detail[key]
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { amount?: unknown }).amount === 'string' &&
    typeof (value as { currency?: unknown }).currency === 'string'
  ) {
    return value as MoneyLike
  }
  return undefined
}

export function detailString(entry: StatementEntry, key: string): string | undefined {
  const value = entry.detail[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export function formatStatementDate(value: string): string {
  return analyticsDate(value)
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: ANALYTICS_TIME_ZONE,
  }).format(new Date(value))
}

function formatMoney(value: MoneyLike): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: value.currency,
  }).format(Number(value.amount))
}
